<script setup>
import { ref, computed, watch } from 'vue'
import { collection, doc, getDocs, limit, orderBy, query, updateDoc } from 'firebase/firestore'
import { getFirebaseDb } from '../../../core/firebase/firebase.js'
import { updateUserProfile } from '../../../core/services/users.js'
import LoadingSpinner from '../LoadingSpinner.vue'

// ============================================
// Props & Emits
// ============================================
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  userUid: {
    type: String,
    default: '',
  },
  currentClientMakat: {
    type: String,
    default: null,
  },
  currentClientName: {
    type: String,
    default: null,
  },
  mode: {
    type: String,
    default: 'primary', // 'primary' | 'sub'
  },
  excludedMakats: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['close', 'linked'])

// ============================================
// State
// ============================================
const isLoadingClients = ref(false)
const clients = ref([])
const searchQuery = ref('')
const error = ref(null)
const linkingClientMakat = ref(null) // Track which client is being linked

// ============================================
// Load Clients on Open
// ============================================
watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      // Reset state when modal opens
      searchQuery.value = ''
      error.value = null
      linkingClientMakat.value = null

      // Load clients if not already loaded
      if (clients.value.length === 0) {
        await loadClients()
      }
    }
  },
  { immediate: true }
)

// ============================================
// Load Clients from Firestore
// ============================================
async function loadClients() {
  isLoadingClients.value = true
  error.value = null

  try {
    const db = getFirebaseDb()
    const clientsRef = collection(db, 'ClientsV2')
    const q = query(clientsRef, orderBy('makat', 'asc'))
    const snapshot = await getDocs(q)

    clients.value = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))
  } catch (err) {
    console.error('Failed to load clients:', err)
    error.value = 'שגיאה בטעינת הלקוחות'
  } finally {
    isLoadingClients.value = false
  }
}

// ============================================
// Filtered Clients (in-memory search)
// ============================================
const filteredClients = computed(() => {
  if (!searchQuery.value.trim()) {
    return clients.value
  }

  const query = searchQuery.value.trim().toLowerCase()

  return clients.value.filter((client) => {
    const name = (client.name || '').toLowerCase()
    const makat = (client.makat || '').toLowerCase()
    const phone = (client.phone || '').toLowerCase()
    const city = (client.city || '').toLowerCase()
    const vat = (client.vat || '').toLowerCase()
    const agent = String(client.agent || '').toLowerCase()

    return (
      name.includes(query) ||
      makat.includes(query) ||
      phone.includes(query) ||
      city.includes(query) ||
      vat.includes(query) ||
      agent.includes(query)
    )
  })
})

// ============================================
// Already-linked set (primary + excluded sub-clients)
// ============================================
const alreadyLinkedSet = computed(() => {
  const set = new Set()
  if (props.currentClientMakat) set.add(String(props.currentClientMakat))
  for (const m of props.excludedMakats || []) {
    if (m != null) set.add(String(m))
  }
  return set
})

function isAlreadyLinked(makat) {
  return alreadyLinkedSet.value.has(String(makat))
}

// ============================================
// Link Client to User
// ============================================
async function linkClient(client) {
  if (isAlreadyLinked(client.makat)) return
  linkingClientMakat.value = client.makat
  error.value = null

  try {
    if (props.mode === 'sub') {
      // Sub-client mode: parent owns the sub_clients array. Just emit.
      emit('linked', {
        makat: String(client.makat),
        name: String(client.name || ''),
        client_makat: String(client.makat),
        client_name: String(client.name || ''),
      })
      emit('close')
      return
    }

    // Primary mode (unchanged):
    // Only update Firestore if userUid exists (edit mode)
    // Skip update for create mode (when userUid is empty)
    if (props.userUid) {
      await updateUserProfile(props.userUid, {
        client_makat: String(client.makat),
        client_name: String(client.name || ''),
      })
    }

    // Set first_order: true if client has no Orders subcollection
    const db = getFirebaseDb()
    const ordersRef = collection(db, 'ClientsV2', String(client.makat), 'Orders')
    const ordersSnap = await getDocs(query(ordersRef, limit(1)))
    if (ordersSnap.empty) {
      await updateDoc(doc(db, 'ClientsV2', String(client.makat)), { first_order: true })
    }

    emit('linked', {
      makat: String(client.makat),
      name: String(client.name || ''),
      client_makat: String(client.makat),
      client_name: String(client.name || ''),
    })
    emit('close')
  } catch (err) {
    console.error('Failed to link client:', err)
    error.value = err.message || 'שגיאה בשיוך הלקוח'
  } finally {
    linkingClientMakat.value = null
  }
}

// ============================================
// Clear Search
// ============================================
function clearSearch() {
  searchQuery.value = ''
}

// ============================================
// Close Modal
// ============================================
function closeModal() {
  emit('close')
}

// ============================================
// Format Display Value
// ============================================
function formatValue(value) {
  return value || '—'
}
</script>

<template>
  <Transition name="modal">
    <div v-if="open" class="modal-overlay" @click.self="closeModal">
      <div class="modal client-link-modal">
        <!-- Header -->
        <div class="modal-header">
          <h2 class="modal-title">
            {{ mode === 'sub' ? 'שיוך תת-לקוח' : 'שיוך לקוח למשתמש' }}
          </h2>
          <button class="modal-close" @click="closeModal">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Current Link Status (primary mode only) -->
        <div v-if="mode !== 'sub'" class="current-link-status">
          <span class="status-label">משויך כרגע:</span>
          <span v-if="currentClientMakat" class="status-value linked">
            {{ currentClientMakat }} — {{ currentClientName || 'ללא שם' }}
          </span>
          <span v-else class="status-value not-linked">לא משויך כרגע</span>
        </div>

        <!-- Search Input -->
        <div class="search-container">
          <div class="search-input-wrapper">
            <svg
              class="search-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="חיפוש לפי שם, מק״ט, טלפון, עיר..."
            />
            <button v-if="searchQuery" class="search-clear" @click="clearSearch">
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="error-msg">{{ error }}</div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="isLoadingClients" class="loading-state">
            <LoadingSpinner size="md" />
            <span>טוען לקוחות...</span>
          </div>

          <!-- Empty State -->
          <div v-else-if="clients.length === 0" class="empty-state">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
            <p>לא נמצאו לקוחות</p>
          </div>

          <!-- No Results -->
          <div v-else-if="filteredClients.length === 0" class="empty-state">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <p>לא נמצאו תוצאות עבור "{{ searchQuery }}"</p>
          </div>

          <!-- Clients Table -->
          <div v-else class="table-container">
            <table class="clients-table">
              <thead>
                <tr>
                  <th>שם</th>
                  <th>מק״ט</th>
                  <th>טלפון</th>
                  <th>עיר</th>
                  <th>סוכן</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="client in filteredClients"
                  :key="client.id"
                  :class="{ 'row-current': isAlreadyLinked(client.makat) }"
                >
                  <td class="td-name">{{ formatValue(client.name) }}</td>
                  <td class="td-makat">{{ formatValue(client.makat) }}</td>
                  <td class="td-phone">{{ formatValue(client.phone) }}</td>
                  <td class="td-city">{{ formatValue(client.city) }}</td>
                  <td class="td-agent">{{ formatValue(client.agent) }}</td>
                  <td class="td-action">
                    <button
                      class="btn-link"
                      :class="{ 'btn-current': isAlreadyLinked(client.makat) }"
                      :disabled="linkingClientMakat !== null || isAlreadyLinked(client.makat)"
                      @click="linkClient(client)"
                    >
                      <LoadingSpinner v-if="linkingClientMakat === client.makat" size="sm" />
                      <span v-else-if="isAlreadyLinked(client.makat)">משויך</span>
                      <span v-else>שייך</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <span class="results-count">{{ filteredClients.length }} לקוחות</span>
          <button class="btn-cancel" @click="closeModal">סגור</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

/* Modal Container */
.client-link-modal {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.modal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: var(--border-radius);
  cursor: pointer;
  color: var(--text-secondary);
  transition: all var(--transition-fast);
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Current Link Status */
.current-link-status {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
  font-size: var(--font-size-sm);
}

.status-label {
  color: var(--text-secondary);
}

.status-value {
  font-weight: var(--font-weight-medium);
}

.status-value.linked {
  color: var(--color-success);
}

.status-value.not-linked {
  color: var(--text-secondary);
  font-style: italic;
}

/* Search Container */
.search-container {
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  right: 12px;
  color: var(--text-secondary);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-lg);
  padding-right: 40px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-primary);
  transition: all var(--transition-fast);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.search-clear {
  position: absolute;
  left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: var(--bg-secondary);
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  color: var(--text-secondary);
  transition: all var(--transition-fast);
}

.search-clear:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Error Message */
.error-msg {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: #fef2f2;
  color: #dc2626;
  font-size: var(--font-size-sm);
  border-bottom: 1px solid #fecaca;
}

/* Modal Body */
.modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  color: var(--text-secondary);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  color: var(--text-secondary);
}

.empty-state svg {
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: var(--font-size-sm);
}

/* Table Container */
.table-container {
  flex: 1;
  overflow: auto;
}

/* Clients Table */
.clients-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.clients-table th {
  position: sticky;
  top: 0;
  background: var(--bg-secondary);
  padding: var(--spacing-sm) var(--spacing-md);
  text-align: right;
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
}

.clients-table td {
  padding: var(--spacing-sm) var(--spacing-md);
  border-bottom: 1px solid var(--border-color-light);
  color: var(--text-primary);
}

.clients-table tr:hover {
  background: var(--bg-secondary);
}

.clients-table tr.row-current {
  background: rgba(16, 185, 129, 0.05);
}

.clients-table tr.row-current:hover {
  background: rgba(16, 185, 129, 0.1);
}

.td-name {
  font-weight: var(--font-weight-medium);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.td-makat {
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.td-phone,
.td-city,
.td-agent {
  color: var(--text-secondary);
  font-size: var(--font-size-xs);
}

.td-action {
  text-align: center;
  width: 80px;
}

/* Link Button */
.btn-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: 4px 12px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  background: var(--gradient-primary);
  color: var(--color-white);
  transition: all var(--transition-fast);
  min-width: 60px;
}

.btn-link:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.btn-link:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-link.btn-current {
  background: var(--color-success);
  cursor: default;
}

.btn-link.btn-current:hover {
  transform: none;
  box-shadow: none;
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.results-count {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.btn-cancel {
  padding: var(--spacing-sm) var(--spacing-lg);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-cancel:hover {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
}

/* Modal Transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .client-link-modal,
.modal-leave-active .client-link-modal {
  transition: transform 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .client-link-modal,
.modal-leave-to .client-link-modal {
  transform: scale(0.95);
}

/* Responsive */
@media (max-width: 600px) {
  .modal-overlay {
    padding: var(--spacing-sm);
  }

  .client-link-modal {
    max-height: 90vh;
  }

  .clients-table th,
  .clients-table td {
    padding: var(--spacing-xs) var(--spacing-sm);
  }

  .td-city,
  .td-agent {
    display: none;
  }
}
</style>
